import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../../store";
import { useMsal } from "@azure/msal-react";
import Sidebar from "../../../../components/Sidebar";
import styles from "./candidates.module.scss";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DescriptionIcon from "@mui/icons-material/Description";
import ClearIcon from "@mui/icons-material/Clear";
import PageLoader from "../../../../components/PageLoader";
import {
  fetchJobCandidates,
  Candidate,
  sendInterviewLink,
  InterviewLinkResponse,
  handleInvitationAction,
  exportCandidates,
  exportInterviewReport,
} from "../../../../store/interviewHostSlice";
import RoleContextModal from "./RoleContextModal";
import InterviewConfigModal from "./InterviewConfigModal";
import RejectCandidateModal from "./RejectCandidateModal";
import InterviewLinkModal from "./InterviewLinkModal";
import ConfirmCancelModal from "./ConfirmCancelModal";

type StatusFilter = "All" | "Approved" | "Invited" | "ReportReady" | "Decided";

const Candidates: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { instance } = useMsal();
  const user = useSelector((state: RootState) => state.auth.user);
  const { candidates, candidatesLoading, candidatesError } = useSelector(
    (state: RootState) => state.interviewHost,
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(
    searchParams.get("search") || "",
  );
  const [activeStatus, setActiveStatus] = useState<StatusFilter>(
    (searchParams.get("status") as StatusFilter) || "All",
  );
  const [expandedCandidateId, setExpandedCandidateId] = useState<string | null>(
    null,
  );
  const [roleContextModalOpen, setRoleContextModalOpen] = useState(false);
  const [interviewConfigModalOpen, setInterviewConfigModalOpen] =
    useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [interviewLinkModalOpen, setInterviewLinkModalOpen] = useState(false);
  const [selectedCandidateForReject, setSelectedCandidateForReject] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedCandidateForConfig, setSelectedCandidateForConfig] = useState<
    string | null
  >(null);
  const [interviewLinkData, setInterviewLinkData] =
    useState<InterviewLinkResponse | null>(null);
  const [sendingInterview, setSendingInterview] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
  }>({
    open: false,
    message: "",
  });
  const [confirmCancelModal, setConfirmCancelModal] = useState<{
    open: boolean;
    candidateId: string;
    candidateName: string;
  }>({
    open: false,
    candidateId: "",
    candidateName: "",
  });
  const [cancellingInvite, setCancellingInvite] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null);

  const debounceTimerRef = useRef<number | null>(null);

  // Fetch candidates with current filters
  const fetchCandidatesWithFilters = useCallback(() => {
    if (jobId) {
      const search = searchParams.get("search") || undefined;
      const status = searchParams.get("status") || undefined;
      dispatch(fetchJobCandidates({ jobId, search, status }));
    }
  }, [jobId, searchParams, dispatch]);

  // Initial fetch and refetch when URL params change
  useEffect(() => {
    fetchCandidatesWithFilters();
  }, [fetchCandidatesWithFilters]);

  const handleLogout = useCallback(async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: "/",
      });
      window.location.href = "/";
    } catch (err) {
      console.error("Logout failed:", err);
    }
  }, [instance]);

  const handleBackToJobs = useCallback(() => {
    navigate("/agent/interview-host-agent/jobs");
  }, [navigate]);

  const handleBackToHome = useCallback(() => {
    navigate("/agent/interview-host-agent");
  }, [navigate]);

  // Handle search input with debouncing
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchInput(value);

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer for debounced search
      debounceTimerRef.current = setTimeout(() => {
        const newParams = new URLSearchParams(searchParams);
        if (value.trim()) {
          newParams.set("search", value.trim());
        } else {
          newParams.delete("search");
        }
        setSearchParams(newParams);
      }, 500); // 500ms debounce delay
    },
    [searchParams, setSearchParams],
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    setSearchInput("");
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("search");
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Handle status filter change
  const handleStatusChange = useCallback(
    (status: StatusFilter) => {
      setActiveStatus(status);
      const newParams = new URLSearchParams(searchParams);

      if (status === "All") {
        newParams.delete("status");
      } else {
        newParams.set("status", status);
      }

      setSearchParams(newParams);
      // Reset expanded state when changing filters
      setExpandedCandidateId(null);
    },
    [searchParams, setSearchParams],
  );

  // Clear all filters
  const handleClearAllFilters = useCallback(() => {
    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Reset state
    setSearchInput("");
    setActiveStatus("All");

    // Clear URL params
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const toggleCandidateExpanded = useCallback((candidateId: string) => {
    setExpandedCandidateId((prev) =>
      prev === candidateId ? null : candidateId,
    );
  }, []);

  const handleRejectClick = useCallback(
    (candidateId: string, candidateName: string) => {
      setSelectedCandidateForReject({ id: candidateId, name: candidateName });
      setRejectModalOpen(true);
    },
    [],
  );

  const handleRejectModalClose = useCallback(() => {
    setRejectModalOpen(false);
    setSelectedCandidateForReject(null);
  }, []);

  const handleConfigureClick = useCallback((candidateId: string) => {
    setSelectedCandidateForConfig(candidateId);
    setInterviewConfigModalOpen(true);
  }, []);

  const handleConfigModalClose = useCallback(() => {
    setInterviewConfigModalOpen(false);
    setSelectedCandidateForConfig(null);
  }, []);

  const handleSendInterviewLink = useCallback(
    async (candidateId: string) => {
      if (!jobId) return;

      setSendingInterview(true);
      try {
        const result = await dispatch(
          sendInterviewLink({ candidateId, jobId }),
        ).unwrap();
        setInterviewLinkData(result);
        setInterviewLinkModalOpen(true);
      } catch (error) {
        console.error("Failed to send interview link:", error);
      } finally {
        setSendingInterview(false);
      }
    },
    [dispatch, jobId],
  );

  const handleInterviewLinkModalClose = useCallback(() => {
    setInterviewLinkModalOpen(false);
    setInterviewLinkData(null);
  }, []);

  const showToast = useCallback((message: string) => {
    setToast({ open: true, message });
    setTimeout(() => {
      setToast({ open: false, message: "" });
    }, 4000);
  }, []);

  const handleRemindAction = useCallback(
    async (candidateId: string, candidateName: string) => {
      if (!jobId) return;

      try {
        await dispatch(
          handleInvitationAction({ candidateId, jobId, action: "remind" }),
        ).unwrap();
        showToast(`Reminder sent to ${candidateName}`);
      } catch (error) {
        console.error("Failed to send reminder:", error);
      }
    },
    [dispatch, jobId, showToast],
  );

  const handleResendAction = useCallback(
    async (candidateId: string) => {
      if (!jobId) return;

      try {
        await dispatch(
          handleInvitationAction({ candidateId, jobId, action: "resend" }),
        ).unwrap();
        showToast("Link regenerated and resent");
      } catch (error) {
        console.error("Failed to resend link:", error);
      }
    },
    [dispatch, jobId, showToast],
  );

  const handleCancelClick = useCallback(
    (candidateId: string, candidateName: string) => {
      setConfirmCancelModal({ open: true, candidateId, candidateName });
    },
    [],
  );

  const handleCancelConfirm = useCallback(async () => {
    if (!jobId || !confirmCancelModal.candidateId) return;

    setCancellingInvite(true);
    try {
      await dispatch(
        handleInvitationAction({
          candidateId: confirmCancelModal.candidateId,
          jobId,
          action: "cancel",
        }),
      ).unwrap();
      setConfirmCancelModal({
        open: false,
        candidateId: "",
        candidateName: "",
      });
      showToast("Interview invitation cancelled");
    } catch (error) {
      console.error("Failed to cancel invitation:", error);
    } finally {
      setCancellingInvite(false);
    }
  }, [dispatch, jobId, confirmCancelModal.candidateId, showToast]);

  const handleCancelModalClose = useCallback(() => {
    if (!cancellingInvite) {
      setConfirmCancelModal({
        open: false,
        candidateId: "",
        candidateName: "",
      });
    }
  }, [cancellingInvite]);

  const handleExport = useCallback(async () => {
    if (!jobId) return;

    setExporting(true);
    try {
      await dispatch(exportCandidates({ jobId })).unwrap();
      showToast("Candidates exported successfully");
    } catch (error) {
      showToast("Failed to export candidates");
      console.error("Failed to export candidates:", error);
    } finally {
      setExporting(false);
    }
  }, [dispatch, jobId, showToast]);

  const handleDownloadReport = useCallback(async (candidate: Candidate) => {
    // Find session_id from candidate data
    // Note: Assuming session_id comes from invite or needs to be fetched
    // For now, using candidate_id as fallback - this may need adjustment based on actual API
    const sessionId = candidate.candidate_id; // Update this based on actual session_id location

    setDownloadingReportId(candidate.candidate_id);
    try {
      const blob = await dispatch(
        exportInterviewReport({ sessionId })
      ).unwrap();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Interview_Report_${candidate.name.replace(/\s+/g, "_")}_${sessionId}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast("Report downloaded successfully");
    } catch (error) {
      showToast("Failed to download report");
      console.error("Failed to download report:", error);
    } finally {
      setDownloadingReportId(null);
    }
  }, [dispatch, showToast]);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "Approved":
        return styles.statusApproved;
      case "Invited":
        return styles.statusInvited;
      case "ReportReady":
        return styles.statusReportReady;
      case "Decided":
        return styles.statusDecided;
      default:
        return styles.statusDefault;
    }
  };

  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (index: number): string => {
    const colors = [
      styles.avatarBlue,
      styles.avatarOrange,
      styles.avatarTeal,
      styles.avatarPurple,
    ];
    return colors[index % colors.length];
  };

  if (candidatesLoading) {
    return <PageLoader label="Loading candidates..." activeNav="home" />;
  }

  if (candidatesError || !candidates) {
    return (
      <div className={styles.container}>
        <Sidebar
          activeNav="home"
          user={user || undefined}
          onLogout={handleLogout}
        />
        <main className={styles.mainContent}>
          <div className={styles.errorContainer}>
            <h2>Failed to load candidates</h2>
            <p>{candidatesError || "An unexpected error occurred."}</p>
            <button onClick={fetchCandidatesWithFilters}>Try Again</button>
          </div>
        </main>
      </div>
    );
  }

  const { job, role_context, interview_configuration, status_counts } =
    candidates;

  return (
    <div className={styles.container}>
      <Sidebar
        activeNav="home"
        user={user || undefined}
        onLogout={handleLogout}
      />
      <main className={styles.mainContent}>
        {/* Breadcrumb */}
        <div className={styles.breadcrumb}>
          <button className={styles.breadcrumbLink} onClick={handleBackToHome}>
            Home
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <button className={styles.breadcrumbLink} onClick={handleBackToJobs}>
            Jobs
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <span className={styles.breadcrumbCurrent}>{job.title}</span>
        </div>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>Candidates</h1>
            <p className={styles.subtitle}>
              {job.title} · {job.department} · {job.location}
            </p>
          </div>
          <div className={styles.headerRight}>
            <button className={styles.btnSecondary}>
              <SettingsIcon sx={{ fontSize: 18 }} />
              Configure interview
            </button>
            {/* Temporarily disabled - Re-run screening */}
            {/* <button className={styles.btnSecondary}>
              <RefreshIcon sx={{ fontSize: 18 }} />
              Re-run screening
            </button> */}
          </div>
        </div>

        {/* Role Context */}
        <div className={styles.roleContextCard}>
          <div className={styles.roleContextHeader}>
            <div className={styles.roleContextIcon}>⚙️</div>
            <div className={styles.roleContextTitle}>
              Role context{" "}
              <button
                className={styles.editLink}
                onClick={() => setRoleContextModalOpen(true)}
              >
                edit
              </button>
            </div>
          </div>
          <p className={styles.roleContextText}>{role_context.text}</p>
          <div className={styles.roleContextSource}>
            <span className={styles.sourceIcon}>ℹ️</span>
            Fetched from {role_context.source} · editable
          </div>
        </div>

        {/* Search and Filters */}
        <div className={styles.searchSection}>
          <div className={styles.searchBox}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by name or candidate ID..."
              value={searchInput}
              onChange={handleSearchChange}
            />
            {searchInput && (
              <button
                className={styles.searchClear}
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                <ClearIcon sx={{ fontSize: 18 }} />
              </button>
            )}
          </div>
          <div className={styles.searchActions}>
            <button className={styles.btnIcon}>
              <FilterListIcon sx={{ fontSize: 20 }} />
              Filter
            </button>
            <button
              className={styles.btnIcon}
              onClick={handleExport}
              disabled={exporting}
            >
              <FileDownloadIcon sx={{ fontSize: 20 }} />
              {exporting ? "Exporting..." : "Export"}
            </button>
          </div>
        </div>

        {/* Status Tabs */}
        <div className={styles.statusTabs}>
          {(Object.keys(status_counts) as StatusFilter[]).map((status) => (
            <button
              key={status}
              className={`${styles.statusTab} ${
                activeStatus === status ? styles.statusTabActive : ""
              }`}
              onClick={() => handleStatusChange(status)}
            >
              {status === "ReportReady" ? "Report ready" : status}
              <span className={styles.statusCount}>
                {status_counts[status]}
              </span>
            </button>
          ))}
        </div>

        {/* Loading Overlay for Filtering */}
        {candidatesLoading && candidates && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}>
              <RefreshIcon className={styles.spinIcon} />
              <span>Loading...</span>
            </div>
          </div>
        )}

        {/* Candidates List */}
        <div className={styles.candidatesList}>
          {candidates.candidates.length === 0 ? (
            <div className={styles.emptyState}>
              <SearchIcon sx={{ fontSize: 48, color: "#adb5bd" }} />
              <h3>No candidates found</h3>
              <p>
                {searchInput
                  ? `No results for "${searchInput}"`
                  : activeStatus !== "All"
                    ? `No candidates with status "${activeStatus === "ReportReady" ? "Report ready" : activeStatus}"`
                    : "No candidates available for this job"}
              </p>
              {(searchInput || activeStatus !== "All") && (
                <button
                  className={styles.btnSecondary}
                  onClick={handleClearAllFilters}
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            candidates.candidates.map((candidate, index) => (
              <div
                key={candidate.candidate_id}
                className={styles.candidateCard}
              >
                {/* Candidate Header - Always Visible */}
                <div
                  className={styles.candidateHeader}
                  onClick={() =>
                    toggleCandidateExpanded(candidate.candidate_id)
                  }
                >
                  <div className={styles.candidateLeft}>
                    <div
                      className={`${styles.avatar} ${getAvatarColor(index)}`}
                    >
                      {getInitials(candidate.name)}
                    </div>
                    <div className={styles.candidateInfo}>
                      <h3 className={styles.candidateName}>{candidate.name}</h3>
                      <p className={styles.candidateMeta}>
                        {candidate.candidate_id} · applied{" "}
                        {candidate.applied_days_ago} days ago
                      </p>
                    </div>
                  </div>
                  <div className={styles.candidateRight}>
                    <div className={styles.matchScore}>
                      <span className={styles.matchPercentage}>
                        {candidate.jd_match}%
                      </span>
                      <span className={styles.matchLabel}>
                        {candidate.overall_score ? "overall score" : "JD match"}
                      </span>
                    </div>
                    <div className={styles.matchBar}>
                      <div
                        className={styles.matchBarFill}
                        style={{ width: `${candidate.jd_match}%` }}
                      />
                    </div>
                    <div
                      className={`${styles.statusBadge} ${getStatusColor(candidate.status)}`}
                    >
                      {candidate.status === "ReportReady"
                        ? "Report ready"
                        : candidate.status}
                    </div>
                    {expandedCandidateId === candidate.candidate_id ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedCandidateId === candidate.candidate_id && (
                  <div className={styles.candidateExpanded}>
                    {/* For Approved Status */}
                    {candidate.status === "Approved" && (
                      <>
                        <div className={styles.expandedRow}>
                          <div className={styles.expandedCol}>
                            <div className={styles.sectionTitle}>
                              <span className={styles.sectionIcon}>📋</span>
                              SCREENING AGENT OUTPUT
                            </div>
                            <div className={styles.screeningOutput}>
                              {candidate.screening_output &&
                              candidate.screening_output.length > 0 ? (
                                candidate.screening_output.map((output, i) => (
                                  <div
                                    key={i}
                                    className={`${styles.outputItem} ${
                                      output.type === "strength"
                                        ? styles.outputStrength
                                        : styles.outputGap
                                    }`}
                                  >
                                    {output.type === "strength" ? (
                                      <CheckCircleIcon
                                        className={styles.outputIcon}
                                      />
                                    ) : (
                                      <WarningIcon
                                        className={styles.outputIcon}
                                      />
                                    )}
                                    <div>
                                      <p className={styles.outputText}>
                                        {output.text}
                                      </p>
                                      {output.evidence && (
                                        <p className={styles.outputEvidence}>
                                          <em>CV:</em> "{output.evidence}"
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <p
                                  style={{
                                    color: "#6c757d",
                                    fontSize: "0.875rem",
                                    fontStyle: "italic",
                                  }}
                                >
                                  No screening output available
                                </p>
                              )}
                            </div>
                          </div>

                          <div className={styles.expandedCol}>
                            <div className={styles.sectionTitle}>
                              <span className={styles.sectionIcon}>💬</span>
                              ADD CONTEXT FOR THIS INTERVIEW
                            </div>
                            <div className={styles.interviewContext}>
                              <p>
                                {candidate.interview_context ||
                                  "No context provided"}
                              </p>
                            </div>
                            {candidate.interview_configuration && (
                              <div className={styles.interviewConfig}>
                                {candidate.interview_configuration
                                  .duration_minutes && (
                                  <div className={styles.configItem}>
                                    <span className={styles.configIcon}>
                                      ⏱️
                                    </span>
                                    {
                                      candidate.interview_configuration
                                        .duration_minutes
                                    }{" "}
                                    min
                                  </div>
                                )}
                                {candidate.interview_configuration.language && (
                                  <div className={styles.configItem}>
                                    <span className={styles.configIcon}>
                                      🌐
                                    </span>
                                    {candidate.interview_configuration.language}
                                  </div>
                                )}
                                {candidate.interview_configuration
                                  .mandatory_questions !== undefined && (
                                  <div className={styles.configItem}>
                                    <span className={styles.configIcon}>
                                      ⭐
                                    </span>
                                    {
                                      candidate.interview_configuration
                                        .mandatory_questions
                                    }{" "}
                                    mandatory Qs
                                  </div>
                                )}
                                {candidate.interview_configuration
                                  .focus_areas && (
                                  <div className={styles.configItem}>
                                    <span className={styles.configIcon}>
                                      🎯
                                    </span>
                                    {
                                      candidate.interview_configuration
                                        .focus_areas.length
                                    }{" "}
                                    focus areas
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className={styles.candidateActions}>
                          {/* Temporarily disabled - View screening report */}
                          {/* <button
                            className={styles.btnLink}
                            onClick={() =>
                              window.open(
                                candidate.screening.report_url,
                                "_blank",
                              )
                            }
                          >
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                            View screening report
                          </button> */}
                          <button
                            className={styles.btnLink}
                            onClick={() =>
                              window.open(candidate.resume.url, "_blank")
                            }
                          >
                            <DescriptionIcon sx={{ fontSize: 16 }} />
                            View CV
                          </button>
                          <div className={styles.actionsSpacer} />
                          <button
                            className={styles.btnSecondary}
                            onClick={() =>
                              handleConfigureClick(candidate.candidate_id)
                            }
                          >
                            Configure
                          </button>
                          <button
                            className={styles.btnReject}
                            onClick={() =>
                              handleRejectClick(
                                candidate.candidate_id,
                                candidate.name,
                              )
                            }
                          >
                            Reject
                          </button>
                          <button
                            className={styles.btnPrimary}
                            onClick={() =>
                              handleSendInterviewLink(candidate.candidate_id)
                            }
                            disabled={sendingInterview}
                          >
                            {sendingInterview
                              ? "Sending..."
                              : "Send interview link"}
                          </button>
                        </div>
                      </>
                    )}

                    {/* For Invited Status */}
                    {candidate.status === "Invited" && (
                      <>
                        <div className={styles.inviteStatus}>
                          <span className={styles.inviteIcon}>ℹ️</span>
                          {candidate.invite ? (
                            <>
                              Link sent{" "}
                              {Math.floor(
                                (new Date().getTime() -
                                  new Date(
                                    candidate.invite.sent_at,
                                  ).getTime()) /
                                  (1000 * 60 * 60),
                              )}{" "}
                              hrs ago ·{" "}
                              {candidate.invite.opened_at
                                ? "opened"
                                : "not yet opened"}{" "}
                              · expires in{" "}
                              {Math.floor(
                                (new Date(
                                  candidate.invite.expires_at,
                                ).getTime() -
                                  new Date().getTime()) /
                                  (1000 * 60 * 60),
                              )}{" "}
                              hrs
                            </>
                          ) : (
                            "Invite details are being processed"
                          )}
                        </div>
                        <div className={styles.candidateActions}>
                          <div className={styles.actionsSpacer} />
                          <button
                            className={styles.btnSecondary}
                            disabled={!candidate.invite}
                            onClick={() =>
                              handleRemindAction(
                                candidate.candidate_id,
                                candidate.name,
                              )
                            }
                          >
                            <span>🔔</span> Send reminder
                          </button>
                          <button
                            className={styles.btnSecondary}
                            disabled={!candidate.invite}
                            onClick={() =>
                              handleResendAction(candidate.candidate_id)
                            }
                          >
                            <RefreshIcon sx={{ fontSize: 16 }} />
                            Resend link
                          </button>
                          <button
                            className={styles.btnReject}
                            disabled={!candidate.invite}
                            onClick={() =>
                              handleCancelClick(
                                candidate.candidate_id,
                                candidate.name,
                              )
                            }
                          >
                            Cancel invite
                          </button>
                        </div>
                      </>
                    )}

                    {/* For Report Ready Status */}
                    {candidate.status === "ReportReady" && (
                      <>
                        <div className={styles.reportSummary}>
                          <div className={styles.summaryItem}>
                            <strong>
                              {candidate.overall_score !== null
                                ? `${candidate.overall_score}/100`
                                : "Score pending"}
                            </strong>{" "}
                            overall
                          </div>
                          <div className={styles.summaryItem}>
                            <strong>
                              {candidate.report_summary?.recommendation ||
                                "Pending"}
                            </strong>{" "}
                            recommended
                          </div>
                          <div className={styles.summaryItem}>
                            OTP{" "}
                            {candidate.report_summary?.otp_verified
                              ? "verified"
                              : "not verified"}{" "}
                            · {candidate.report_summary?.person_count ?? "—"}{" "}
                            person ·{" "}
                            {candidate.report_summary?.tab_switches ?? "—"} tab
                            switches
                          </div>
                        </div>
                        <div className={styles.candidateActions}>
                          <div className={styles.actionsSpacer} />
                          <button
                            className={styles.btnSecondary}
                            onClick={() => handleDownloadReport(candidate)}
                            disabled={
                              downloadingReportId === candidate.candidate_id
                            }
                          >
                            <FileDownloadIcon sx={{ fontSize: 16 }} />
                            {downloadingReportId === candidate.candidate_id
                              ? "Downloading..."
                              : "Download"}
                          </button>
                          <button
                            className={styles.btnPrimary}
                            onClick={() =>
                              navigate(
                                `/agent/interview-host-agent/jobs/${jobId}/candidates/${candidate.candidate_id}/report`,
                              )
                            }
                          >
                            Review report
                          </button>
                        </div>
                      </>
                    )}

                    {/* For Decided Status */}
                    {candidate.status === "Decided" && (
                      <>
                        <div className={styles.decisionInfo}>
                          Decision:{" "}
                          <strong>
                            {candidate.decision?.outcome || "Pending"}
                          </strong>
                          {candidate.decision && (
                            <>
                              {" "}
                              · by {candidate.decision.decided_by} on{" "}
                              {new Date(
                                candidate.decision.decided_at,
                              ).toLocaleDateString()}
                            </>
                          )}
                        </div>
                        <div className={styles.candidateActions}>
                          <div className={styles.actionsSpacer} />
                          <button
                            className={styles.btnSecondary}
                            onClick={() =>
                              navigate(
                                `/agent/interview-host-agent/jobs/${jobId}/candidates/${candidate.candidate_id}/report`,
                              )
                            }
                          >
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                            View report
                          </button>
                          <button
                            className={styles.btnSecondary}
                            disabled={!candidate.decision}
                          >
                            <SettingsIcon sx={{ fontSize: 16 }} />
                            Change decision
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </main>

      {/* Modals */}
      {roleContextModalOpen && jobId && (
        <RoleContextModal
          open={roleContextModalOpen}
          onClose={() => setRoleContextModalOpen(false)}
          jobId={jobId}
          currentContext={role_context.text}
        />
      )}

      {interviewConfigModalOpen && selectedCandidateForConfig && jobId && (
        <InterviewConfigModal
          open={interviewConfigModalOpen}
          onClose={handleConfigModalClose}
          candidateId={selectedCandidateForConfig}
          jobId={jobId}
          currentConfig={
            candidates.candidates.find(
              (c) => c.candidate_id === selectedCandidateForConfig,
            )?.interview_configuration || {
              duration_minutes: 25,
              language: "English",
              mandatory_questions: 2,
              focus_areas: [],
              off_limit_topics: [],
            }
          }
          currentContext={
            candidates.candidates.find(
              (c) => c.candidate_id === selectedCandidateForConfig,
            )?.interview_context || ""
          }
        />
      )}

      {rejectModalOpen && selectedCandidateForReject && jobId && (
        <RejectCandidateModal
          open={rejectModalOpen}
          onClose={handleRejectModalClose}
          candidateId={selectedCandidateForReject.id}
          candidateName={selectedCandidateForReject.name}
          jobId={jobId}
        />
      )}

      {interviewLinkModalOpen && interviewLinkData && (
        <InterviewLinkModal
          open={interviewLinkModalOpen}
          onClose={handleInterviewLinkModalClose}
          interviewData={interviewLinkData}
        />
      )}

      {confirmCancelModal.open && (
        <ConfirmCancelModal
          open={confirmCancelModal.open}
          onClose={handleCancelModalClose}
          onConfirm={handleCancelConfirm}
          candidateName={confirmCancelModal.candidateName}
          loading={cancellingInvite}
        />
      )}

      {/* Toast Notification - Bottom Right */}
      {toast.open && (
        <div className={styles.toastContainer}>
          <div className={styles.toast}>
            <CheckCircleIcon className={styles.toastIcon} />
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Candidates;
